import { useTranslation } from "react-i18next";
import { FaCircleCheck } from "react-icons/fa6";
import { css } from "../../../../styled-system/css";
import { Box, Flex, HStack, VStack } from "../../../../styled-system/jsx";
import { Button } from "../../../components/core/basic/Button";
import { PhaseProgressBar } from "./PhaseProgressBar";
import type { IngestionModel, IngestionPhase } from "../types";

type Stage = "connecting" | "arriving" | "finishing" | "ready" | "delayed" | "failed";

// The takeover covers the content panel only — never the nav or sidebar — so the
// user can still change pages. It must stay pinned while the page scrolls, so it's
// position: fixed (viewport-anchored). .main-panel is itself the scroll container,
// so a position: absolute scrim would scroll away with the content instead of
// staying put. We inset it past the navbar (top) and the collapsed sidebar (left).
const NAVBAR_HEIGHT = 75; // px — in-flow navbar at the top of .main-panel
const SIDEBAR_WIDTH = 80; // px — collapsed sidebar (body.sidebar-mini is always set)

const stageOf = (recent: IngestionPhase, model: IngestionModel): Stage => {
  if (model.isReady) return "ready";
  if (model.recentFailure === "blocking") return "failed";
  if (model.recentFailure === "delayed") return "delayed";
  const pct = recent.percent ?? 0;
  if (pct >= 100) return "finishing";
  return pct < 4 ? "connecting" : "arriving";
};

export const FirstRunTakeover = ({
  model,
  onEnter,
  spApiMissing,
}: {
  model: IngestionModel;
  onEnter: () => void;
  /** Amazon SP-API not authorized: only advertising data flows, no sales/orders/inventory. */
  spApiMissing?: boolean;
}) => {
  const { t } = useTranslation(["onboarding"]);

  const recent = model.phases.find((p) => p.key === "recent")!;
  const pct = recent.percent ?? 0;
  const stage = stageOf(recent, model);
  const days = Math.min(90, Math.max(0, Math.round((pct / 100) * 90)));

  // Without SP-API the recent workflow only has advertising data, so the meter
  // and copy describe advertising — not "sales, catalog, inventory."
  const recentLabel =
    spApiMissing ? t(($) => $.onboarding["Advertising performance"]) : t(($) => $.onboarding["Recent performance"]);

  const copy = {
    connecting: {
      eyebrow: t(($) => $.onboarding["Setting up"]),
      headline: t(($) => $.onboarding["Connecting to your account"]),
      sub: t(
        ($) =>
          $.onboarding[
            "We're verifying your connection and mapping your linked channels. Nothing to show just yet — this only takes a moment."
          ],
      ),
      progLabel: t(($) => $.onboarding["Verifying connection"]),
      progSub: t(($) => $.onboarding["Checking credentials & linked channels"]),
    },
    arriving: {
      eyebrow: t(($) => $.onboarding["Importing your data"]),
      headline: t(($) => $.onboarding["Building your dashboard"]),
      sub:
        spApiMissing ?
          t(
            ($) =>
              $.onboarding[
                "Your advertising data from the last 90 days is loading now — enough to start optimizing. Connect Amazon's Selling Partner API to add sales, orders, and inventory."
              ],
          )
        : t(
            ($) =>
              $.onboarding[
                "Sales, catalog, inventory, and advertising from the last 90 days are loading now — enough to start working while the rest loads in the background."
              ],
          ),
      progLabel: recentLabel,
      progSub: t(($) => $.onboarding["{count} of 90 days loaded"], { count: days }),
    },
    finishing: {
      eyebrow: t(($) => $.onboarding["Importing your data"]),
      headline: t(($) => $.onboarding["Finishing up the last 90 days"]),
      sub: t(
        ($) =>
          $.onboarding["Your last 90 days are in — we're finalizing your dashboard now. This only takes a moment."],
      ),
      progLabel: recentLabel,
      progSub: t(($) => $.onboarding["Wrapping up"]),
    },
    ready: {
      eyebrow: t(($) => $.onboarding["Ready to go"]),
      headline: t(($) => $.onboarding["Your dashboard is ready"]),
      sub:
        spApiMissing ?
          t(
            ($) =>
              $.onboarding[
                "Your advertising data is loaded. Authorize the Selling Partner API to add sales, orders, and inventory."
              ],
          )
        : t(
            ($) =>
              $.onboarding[
                "Your last 90 days of data is loaded. We'll keep loading your full history and extra insights in the background."
              ],
          ),
      progLabel: spApiMissing ? recentLabel : t(($) => $.onboarding["Last 90 days"]),
      progSub: t(($) => $.onboarding["Ready to explore"]),
    },
    delayed: {
      eyebrow: t(($) => $.onboarding["Importing your data"]),
      headline: t(($) => $.onboarding["This is taking a little longer than usual"]),
      sub: t(
        ($) =>
          $.onboarding[
            "We're still importing your last 90 days of data — this occasionally takes longer. No action needed; it'll appear automatically."
          ],
      ),
      progLabel: recentLabel,
      progSub: t(($) => $.onboarding["Working on it…"]),
    },
    failed: {
      eyebrow: t(($) => $.onboarding["Importing your data"]),
      headline: t(($) => $.onboarding["We hit a snag importing your data"]),
      sub: t(($) => $.onboarding["Something went wrong importing your data. Please contact support."]),
      progLabel: recentLabel,
      progSub: "",
    },
  }[stage];

  const showButtons = stage === "ready" || stage === "failed";
  const showPercent = stage === "arriving" || stage === "finishing" || stage === "ready";

  return (
    <Box
      role="dialog"
      aria-modal="true"
      style={{ top: NAVBAR_HEIGHT, left: SIDEBAR_WIDTH }}
      className={css({
        position: "fixed",
        right: 0,
        bottom: 0,
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "large",
        pointerEvents: "none",
        backgroundColor: "appBackground/25",
      })}
    >
      <VStack
        alignItems="stretch"
        gap="medium"
        width="548px"
        maxWidth="92%"
        backgroundColor="white"
        borderRadius="large"
        boxShadow="0 16px 44px rgba(45, 27, 78, 0.15)"
        padding="xlarge"
        pointerEvents="auto"
      >
        <Box fontSize="small" fontWeight="bold" letterSpacing="0.15em" textTransform="uppercase" color="fuchsia">
          {copy.eyebrow}
        </Box>
        <h1 className={css({ margin: 0, fontSize: "xxlarge", fontWeight: "bold", color: "text.heading" })}>
          {copy.headline}
        </h1>
        <Box fontSize="medium" color="text.secondary" lineHeight="1.55">
          {copy.sub}
        </Box>

        <VStack
          alignItems="stretch"
          gap="small"
          backgroundColor="appBackground"
          border="1px solid {colors.border.default}"
          borderRadius="medium"
          padding="medium"
        >
          <Flex alignItems="center" justifyContent="space-between" gap="small">
            <HStack gap="small" alignItems="center">
              {stage === "ready" && <FaCircleCheck size={20} className={css({ color: "green.600" })} />}
              <Box fontSize="small" fontWeight="bold" color="text.heading">
                {copy.progLabel}
              </Box>
            </HStack>
            {showPercent && (
              <Box fontSize="small" fontWeight="bold" color="royalPurple">
                {pct}%
              </Box>
            )}
          </Flex>
          {showPercent && <PhaseProgressBar percent={pct} height={8} />}
          {copy.progSub && (
            <Box fontSize="small" color="text.secondary">
              {copy.progSub}
            </Box>
          )}
        </VStack>

        {stage === "arriving" && (
          <Box fontSize="small" color="text.secondary" lineHeight="1.5">
            {spApiMissing ?
              t(
                ($) =>
                  $.onboarding[
                    "Next up: authorize the Selling Partner API to bring in your sales, orders, and full history."
                  ],
              )
            : t(
                ($) =>
                  $.onboarding[
                    "Next up: we'll keep loading the rest of your 2-year sales history, then bring in page views and search query performance."
                  ],
              )
            }
          </Box>
        )}

        {showButtons && (
          <HStack justifyContent="center">
            <Button onPress={onEnter}>{t(($) => $.onboarding["Explore your dashboard"])}</Button>
          </HStack>
        )}
      </VStack>
    </Box>
  );
};
